import { SearchParamsObject } from '@algolia/client-search';
import debounce from 'lodash/debounce';
import once from 'lodash/once';
//
// Note: this component is currently used
// for cms content article names. It is generalized so other
// Algolia based sources can be used by adding new AutocompleteType options.
import {
  ChangeEvent,
  FocusEvent,
  InputHTMLAttributes,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
// eslint-disable-next-line import/no-namespace
import * as ReactAutosuggest from 'react-autosuggest';

import { Badge } from 'components/Badge/Badge';
import { Text } from 'components/Text/Text';
import { requiredConfig } from 'config/appConfigUtils';
import { PostSearchAlgoliaPublicContentHit } from 'modules/postSearch/algolia/types/PostSearchAlgoliaPublicContentHit';
import { searchAlgoliaConfig } from 'modules/search/algolia/searchAlgoliaConfig';
import { staffApiFetchInvoices } from 'modules/staff/api/general';
import { StaffApiInvoice } from 'modules/staff/api/types/StaffApiInvoice';
import { StaffAlgoliaPostSearchContentHit } from 'modules/staff/postSearch/algolia/types/StaffAlgoliaPostSearchContentHit';
import { fontWeights } from 'theme/theme';
import { algoliaSearch } from 'utils/algolia/algoliaSearch';
import { getIn } from 'utils/functional/object';

import { AutosuggestWrapper } from './Autocomplete.styled';

export type Suggestion = {
  name: string;
  value: string;
  hit: PostSearchAlgoliaPublicContentHit;
  hits: (
    | PostSearchAlgoliaPublicContentHit
    | StaffAlgoliaPostSearchContentHit
  )[];
  invoice?: StaffApiInvoice;
};

const Autosuggest = ReactAutosuggest as unknown as {
  new (): ReactAutosuggest<Suggestion>;
};

export type AutocompleteType =
  | 'CONTENT'
  | 'NEW_ORG_NAME'
  | 'STAFF_ORG_PAYMENTS'
  | 'SALARY_SURVEY_TITLES'
  | 'SALARY_REPORT_SURVEY_TITLES'
  | 'STAFF_SALARY_SURVEY_TITLES'
  | 'STAFF_INVOICE_PAYMENTS'
  | 'STAFF_CONTENT_POSTS';

type AutocompleteOptions = {
  indexName: string;
  queryOptions: SearchParamsObject;
  responseValue: string;
  responseName: string;
};

type AutocompleteOptionsMap = Record<AutocompleteType, AutocompleteOptions>;
// @ts-expect-error fix types
const autocompleteOptionsMap: () => AutocompleteOptionsMap = once(() => ({
  CONTENT: {
    indexName: requiredConfig('algolia', 'contentIndexName'),
    queryOptions: {
      page: 0,
      hitsPerPage: 50,
      attributesToRetrieve: ['objectID', 'title'],
    },
    responseValue: 'objectID',
    responseName: 'title',
  },
  NEW_ORG_NAME: {
    indexName: requiredConfig('algolia', 'siteIndexName'),
    queryOptions: {
      page: 0,
      hitsPerPage: 20,
      filters: 'type:ORG',
      attributesToRetrieve: ['objectId', 'name'],
    },
    responseValue: null,
    responseName: 'name',
  },
  STAFF_ORG_PAYMENTS: {
    indexName: requiredConfig('algolia', 'staffIndexName'),
    queryOptions: {
      hitsPerPage: 50,
      page: 0,
      attributesToRetrieve: ['objectID', 'name'],
      filters: 'type:ORG',
      attributesToHighlight: [],
      getRankingInfo: false,
      clickAnalytics: false,
    },
    responseValue: 'objectID',
    responseName: 'name',
  },
  SALARY_SURVEY_TITLES: {
    indexName: requiredConfig('algolia', 'compdbIndexName'),
    queryOptions: {
      attributesToRetrieve: ['name', 'jobTitle'],
      filters: 'includeInSurvey:true',
      hitsPerPage: 20,
      ignorePlurals: true,
      page: 0,
      queryLanguages: ['en'],
      removeStopWords: true,
      removeWordsIfNoResults: 'allOptional',
    },
    responseValue: null,
    responseName: 'name',
  },
  SALARY_REPORT_SURVEY_TITLES: {
    indexName: requiredConfig('algolia', 'compdbIndexName'),
    queryOptions: {
      attributesToRetrieve: ['name', 'jobTitle'],
      filters: 'includeInSurvey:true',
      hitsPerPage: 20,
      ignorePlurals: true,
      page: 0,
      queryLanguages: ['en'],
      removeStopWords: true,
      removeWordsIfNoResults: 'allOptional',
    },
    responseValue: null,
    responseName: 'name',
  },
  STAFF_SALARY_SURVEY_TITLES: {
    indexName: requiredConfig('algolia', 'compDbStaffIndexName'),
    queryOptions: {
      page: 0,
      hitsPerPage: 50,
      attributesToRetrieve: ['name', 'subFamilyId'],
    },
    responseValue: null,
    responseName: 'name',
  },
  STAFF_CONTENT_POSTS: {
    indexName: requiredConfig('algolia', 'contentStaffIndexName'),
    queryOptions: {
      page: 0,
      hitsPerPage: 20,
      attributesToRetrieve: ['objectID', 'subsiteId', 'pages'],
      filters: 'type:CMS_PAGE_MASTER AND pages.status:PUBLISHED',
    },
    responseValue: 'objectID',
    responseName: 'title',
  },
}));

const staffContentSuggestion = ({ hit }: Suggestion) => (
  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
    <div>
      <Text data-qa-id="autocomplete-suggestion">
        {hit.title}
        <Badge>{hit.pageType}</Badge>
      </Text>
      <Text styleAs="bodySmall" color="lightContentGrey">
        {hit.url}
      </Text>
    </div>
    <div>
      <Text styleAs="bodySmall" color="lightContentGrey">
        {hit.locale}
      </Text>
    </div>
  </div>
);

const getIndex = (autocompleteType: AutocompleteType) =>
  autocompleteOptionsMap()[autocompleteType].indexName;

const getQueryOptions = (
  autocompleteType: AutocompleteType,
  filters?: string,
): SearchParamsObject =>
  ({
    ...autocompleteOptionsMap()[autocompleteType].queryOptions,
    filters: [
      filters,
      autocompleteOptionsMap()[autocompleteType].queryOptions.filters,
    ]
      .filter((a) => Boolean(a))
      .join(' AND '),
  }) satisfies SearchParamsObject;

const responseToOption = (
  response: {
    hits: (
      | PostSearchAlgoliaPublicContentHit
      | StaffAlgoliaPostSearchContentHit
    )[];
  },
  autocompleteType: AutocompleteType,
) => {
  const { responseValue, responseName } =
    autocompleteOptionsMap()[autocompleteType];

  let hits: (
    | PostSearchAlgoliaPublicContentHit
    | StaffAlgoliaPostSearchContentHit
  )[] = [];

  /* Staff Content Index returns data representing all content under master ID.
   * Each page represents a hit.
   */
  if (autocompleteType === 'STAFF_CONTENT_POSTS') {
    hits = response.hits
      .flatMap((hit) => (hit as StaffAlgoliaPostSearchContentHit).pages)
      .filter((a) => Boolean(a)); // Remove nulls, some hits may not have pages
  } else {
    hits = response.hits;
  }

  return hits.map(
    (hit) =>
      ({
        /* Provide the original hit regardless-- the selection is used appropriately. */
        value: responseValue ? getIn(hit, responseValue.split('.')) : hit,
        name: responseName ? getIn(hit, responseName.split('.')) : hit,
        hits: response.hits, // Provide original hits for staff content
        hit,
      }) as Suggestion,
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getSuggestionValue = (suggestion: any) => suggestion.name;

const promiseOptions = async (
  input: string,
  autocompleteType: AutocompleteType,
  filters: string | undefined,
  overrideSearchConfig?: Readonly<{ apiKey: string; appId: string }>,
  onApiKeyTimeout?: () => Promise<string>,
) =>
  algoliaSearch<
    PostSearchAlgoliaPublicContentHit | StaffAlgoliaPostSearchContentHit
  >({
    config: overrideSearchConfig || searchAlgoliaConfig(),
    query: input,
    indexName: getIndex(autocompleteType),
    options: getQueryOptions(autocompleteType, filters),
    analytics: true,
    onApiKeyTimeout,
  });

type Props = {
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (
    event: FocusEvent<HTMLInputElement>,
    suggestions?: Suggestion[],
  ) => void;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSuggestionSelected?: (...args: Array<any>) => any;
  onSuggestionsUpdated?: ({
    suggestions,
    value,
  }: {
    suggestions?: Suggestion[];
    value: string;
  }) => void;
  shouldRenderSuggestions?: (arg0: string) => boolean;
  autocompleteType: AutocompleteType;
  filterOptions?: string;
  hasError: boolean;
  searchConfig?: {
    apiKey: string;
    appId: string;
  };
  large?: boolean;
  value: string;
  placeholder?: string;
  suggestionComponentFn?: ({ name, value, hit }: Suggestion) => ReactNode;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateApiKey?: (...args: Array<any>) => any;
  height?: string;
  'data-qa-id': string;
} & Pick<
  InputHTMLAttributes<HTMLInputElement>,
  'onKeyPress' | 'id' | 'name' | 'type'
>;

export function AutocompleteTextInput({
  onChange,
  onBlur,
  onSuggestionSelected,
  onSuggestionsUpdated,
  shouldRenderSuggestions,
  autocompleteType,
  filterOptions,
  hasError,
  height,
  searchConfig,
  large,
  placeholder,
  suggestionComponentFn,
  updateApiKey,
  ...otherProps
}: Props) {
  const [suggestions, updateSuggestions] = useState<Suggestion[]>([]);

  const [inProgressQuery, updateInProgressQuery] = useState<
    | Promise<
        ReadonlyArray<{
          name: unknown;
          value: unknown;
        }>
      >
    | null
    | undefined
  >(null);

  useEffect(() => {
    if (onSuggestionsUpdated) {
      onSuggestionsUpdated({
        suggestions,
        value: otherProps.value,
      });
    }
  }, [onSuggestionsUpdated, otherProps.value, suggestions]);

  const loadSuggestions = (value: string) => {
    // Cancel the previous request
    if (inProgressQuery) {
      inProgressQuery.then(() => {
        debounce(() => loadSuggestions(value), 0);
      });
    } else {
      let query;
      if (autocompleteType === 'STAFF_INVOICE_PAYMENTS') {
        query = staffApiFetchInvoices({
          number: value,
          outstanding: true,
        }).then((invoices) =>
          invoices.map(
            (i: StaffApiInvoice) =>
              ({
                value: i.id,
                name: i.number.toString(),
                invoice: i,
              }) as Suggestion,
          ),
        );
      } else {
        query = promiseOptions(
          value,
          autocompleteType,
          filterOptions,
          searchConfig,
          updateApiKey,
        ).then((r) => responseToOption(r, autocompleteType));
      }

      query.then((options) => {
        updateSuggestions(options);
        updateInProgressQuery(null);
      });
      updateInProgressQuery(query);

      return query;
    }
  };

  const onSuggestionsFetchRequested = ({ value }: { value: string }) => {
    loadSuggestions(value);
  };

  const onSuggestionsClearRequested = () => {
    updateSuggestions([]);
    updateInProgressQuery(null);
  };

  const onChangeInternal = (
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: Record<string, any>,
    {
      newValue,
    }: {
      newValue: string;
    },
  ) => {
    onChange({
      target: {
        value: newValue,
      },
    } as ChangeEvent<HTMLInputElement>);
  };

  const onBlurInternal = (event: FocusEvent<HTMLElement>) => {
    if (onBlur) {
      onBlur(event as FocusEvent<HTMLInputElement>, suggestions);
    }
  };

  const renderSuggestion = useCallback(
    (suggestion: Suggestion) => {
      if (suggestionComponentFn) {
        return <>{suggestionComponentFn(suggestion)}</>;
      }

      if (autocompleteType === 'STAFF_CONTENT_POSTS') {
        return staffContentSuggestion(suggestion);
      }

      return (
        <Text
          data-qa-id="autocomplete-suggestion"
          fontWeight={large ? fontWeights.semiBold : undefined}
        >
          {suggestion.name}
        </Text>
      );
    },
    [large, autocompleteType, suggestionComponentFn],
  );

  const inputProps = {
    ...otherProps,
    placeholder,
    onChange: onChangeInternal,
    onBlur: onBlurInternal,
  };
  return (
    <AutosuggestWrapper
      $height={height}
      $large={large}
      $borderSize={large ? 4 : 2}
      $hasError={hasError}
    >
      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        onSuggestionSelected={onSuggestionSelected}
        shouldRenderSuggestions={shouldRenderSuggestions}
        renderSuggestion={renderSuggestion}
        inputProps={inputProps}
      />
    </AutosuggestWrapper>
  );
}
